import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { cn } from '@/lib/utils.js'

/**
 * Form field primitives (D4).
 *
 * The whole point of grouping Label + Input + error into one `Field` is that a
 * validation message must be BOUND to its input, not merely near it. The API
 * returns field-level slugs (`missing_field`, `invalid_field`,
 * `missing_contact`), and a message that a screen reader never announces is
 * only half-delivered — `aria-describedby` and `aria-invalid` are what finish
 * the job. Doing it here once means no screen can forget.
 */

export const Label = React.forwardRef(function Label ({ className, ...props }, ref) {
  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn('text-sm font-medium text-ink leading-none', className)}
      {...props}
    />
  )
})

export const Input = React.forwardRef(function Input ({ className, invalid, ...props }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'flex h-tap w-full rounded-md border bg-surface px-3 py-2 text-base',
        // 16px on mobile, because anything smaller makes iOS zoom the viewport
        // on focus and the user has to pinch back out after every field.
        'sm:text-sm',
        'border-line-strong shadow-card',
        'placeholder:text-ink-subtle',
        'transition-[border-color,box-shadow] duration-150',
        'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle',
        invalid && 'border-danger',
        className
      )}
      {...props}
    />
  )
})

/**
 * A labelled input with an optional hint and error, wired together.
 *
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {string} [props.hint] persistent helper text
 * @param {string} [props.error] replaces the hint when present
 */
export function Field ({ id, label, hint, error, className, children, ...props }) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>

      {children ?? (
        <Input
          id={id}
          invalid={Boolean(error)}
          aria-describedby={errorId ?? hintId}
          {...props}
        />
      )}

      {/* The error REPLACES the hint rather than stacking below it: two lines
          of guidance under one field is where users stop reading either. */}
      {error
        ? (
          <p id={errorId} className="text-sm text-danger-ink" role="alert">
            {error}
          </p>
          )
        : hint
          ? <p id={hintId} className="text-sm text-ink-muted">{hint}</p>
          : null}
    </div>
  )
}
